"use client";

import { useState, useEffect, useRef, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { ImageOff } from "lucide-react";

interface ScreenshotGalleryProps {
  topicName: string;
  count?: number;
  lazyLoad?: boolean;
  placeholder?: boolean;
}

function LazyImage({
  src,
  alt,
  className,
  onLoad,
  onError,
  placeholder = true,
}: {
  src: string;
  alt: string;
  className?: string;
  onLoad?: () => void;
  onError?: () => void;
  placeholder?: boolean;
}) {
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [isInView, setIsInView] = useState(false);
  const imgRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      { rootMargin: "100px", threshold: 0.01 }
    );

    if (imgRef.current) {
      observer.observe(imgRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const handleLoad = () => {
    setIsLoaded(true);
    onLoad?.();
  };

  const handleError = () => {
    setHasError(true);
    onError?.();
  };

  if (hasError) {
    return (
      <div ref={imgRef} className={className}>
        <div className="absolute inset-0 flex flex-col items-center justify-center text-muted-foreground bg-muted/50">
          <ImageOff className="w-10 h-10 mb-3 opacity-60" />
          <span className="text-sm mb-1">截图正在生成中</span>
          <span className="text-xs text-muted-foreground">点击可前往微博查看</span>
        </div>
      </div>
    );
  }

  return (
    <div ref={imgRef} className={className}>
      {placeholder && !isLoaded && (
        <Skeleton className="absolute inset-0 animate-pulse" />
      )}
      {isInView && (
        <img
          src={src}
          alt={alt}
          className={`w-full h-full object-cover transition-all duration-500 ${
            isLoaded ? "opacity-100 scale-100" : "opacity-0 scale-95"
          }`}
          onLoad={handleLoad}
          onError={handleError}
          loading="lazy"
          decoding="async"
        />
      )}
    </div>
  );
}

export function ScreenshotGallery({
  topicName,
  count = 12,
  lazyLoad = true,
  placeholder = true,
}: ScreenshotGalleryProps) {
  const [failedImages, setFailedImages] = useState<Set<number>>(new Set());
  const [loadedImages, setLoadedImages] = useState<Set<number>>(new Set());
  const [visibleCount, setVisibleCount] = useState(lazyLoad ? Math.min(count, 6) : count);

  const getScreenshotUrl = useCallback((name: string) => {
    const encodedName = encodeURIComponent(name);
    return `https://cdn.weibotop.cn/real/${encodedName}.png?x-oss-process=style/weibohome`;
  }, []);

  const handleImageLoad = (index: number) => {
    setLoadedImages((prev) => new Set(prev).add(index));
  };

  const handleImageError = (index: number) => {
    setFailedImages((prev) => new Set(prev).add(index));
  };

  const loadMoreRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!lazyLoad || count <= visibleCount) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setVisibleCount((prev) => Math.min(prev + 6, count));
        }
      },
      { rootMargin: "200px", threshold: 0.1 }
    );

    if (loadMoreRef.current) {
      observer.observe(loadMoreRef.current);
    }

    return () => observer.disconnect();
  }, [count, visibleCount, lazyLoad]);

  const screenshotUrl = getScreenshotUrl(topicName);
  const weiboUrl = `https://s.weibo.com/weibo?q=${encodeURIComponent(topicName)}`;

  // 加载失败且无成功图时不显示
  if (failedImages.has(0) && loadedImages.size === 0) {
    return null;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>📸 微博截图</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex justify-center">
          <a
            href={weiboUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="relative w-full max-w-md aspect-[3/4] bg-muted rounded-lg overflow-hidden block"
          >
            <LazyImage
              src={screenshotUrl}
              alt={`${topicName} 截图`}
              className="absolute inset-0"
              onLoad={() => handleImageLoad(0)}
              onError={() => handleImageError(0)}
              placeholder={placeholder}
            />
          </a>
        </div>
      </CardContent>
    </Card>
  );
}
